import { Navigate, useLocation } from 'react-router-dom';
import { Spin } from 'antd';
import type { ReactNode } from 'react';
import type { Module } from '@dtable/rbac';
import { useAuthStore } from '../stores/auth-store';
import { useMe } from '../hooks/use-me';
import { usePermissions } from '../hooks/use-permissions';

interface ProtectedRouteProps {
  children: ReactNode;
  /**
   * Optional module gate — user must have at least one permission on any of
   * these modules to enter the route. Falls back to the sidebar visibility rule.
   */
  requiresModules?: Module[];
}

/**
 * Route guard: bounces unauthenticated users to /login, blocks users whose
 * role doesn't grant access to the module. Deep-link intent is preserved via
 * location.state so post-login we can return to the original URL.
 */
export function ProtectedRoute({ children, requiresModules = [] }: ProtectedRouteProps) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const location = useLocation();
  const meQuery = useMe();
  const { seesModule } = usePermissions();

  if (!accessToken) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  if (meQuery.isPending) {
    return (
      <div style={{ display: 'flex', minHeight: '60vh', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (requiresModules.length > 0 && !requiresModules.some(seesModule)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
